import request from '@/utils/request'
import {
  buildTourLeaderJobQuery,
  requireApplicationStatus,
  requireJobStatus,
  toTourLeaderJobPayload,
} from '@/utils/tourLeaderJob'

const agencyApiBase = '/tour-leader-agency/v1'

function agencyRequest(config) {
  return request({
    ...config,
    preserveHttpResponse: true,
  })
}

function pickDefined(source, allowedFields) {
  return allowedFields.reduce((payload, field) => {
    if (source && source[field] !== undefined) {
      payload[field] = source[field]
    }
    return payload
  }, {})
}

function requirePayloadFields(payload, requiredFields) {
  const missing = requiredFields.some(
    field => payload[field] === undefined || payload[field] === null || payload[field] === ''
  )
  if (missing) throw new TypeError('tour_leader_request_fields_required')
  return payload
}

export function getLeaderProfiles(params) {
  return agencyRequest({
    url: `${agencyApiBase}/profiles`,
    method: 'get',
    params,
  })
}

export function getLeaderProfile(userId) {
  return agencyRequest({
    url: `${agencyApiBase}/profiles/${encodeURIComponent(userId)}`,
    method: 'get',
  })
}

export function getAgencyFeatureTravels() {
  return agencyRequest({
    url: `${agencyApiBase}/travels`,
    method: 'get',
  })
}

export function getAgencyConversations() {
  return agencyRequest({
    url: `${agencyApiBase}/conversations`,
    method: 'get',
  })
}

export function createAgencyConversation(data) {
  return agencyRequest({
    url: `${agencyApiBase}/conversations`,
    method: 'post',
    // AgencyCode is intentionally absent: the API must derive it from the
    // authenticated identity instead of trusting a browser supplied value.
    data: pickDefined(data, [
      'memberUserId',
      'travelId',
      'messageType',
      'content',
      'mediaId',
      'invitationId',
      'clientMessageId',
    ]),
  })
}

export function getAgencyConversationMessages(conversationId, params) {
  return agencyRequest({
    url: `${agencyApiBase}/conversations/${encodeURIComponent(conversationId)}/messages`,
    method: 'get',
    params,
  })
}

export function sendAgencyConversationMessage(conversationId, data) {
  return agencyRequest({
    url: `${agencyApiBase}/conversations/${encodeURIComponent(conversationId)}/messages`,
    method: 'post',
    data: pickDefined(data, ['messageType', 'content', 'mediaId', 'clientMessageId']),
  })
}

export function markAgencyConversationRead(conversationId) {
  return agencyRequest({
    url: `${agencyApiBase}/conversations/${encodeURIComponent(conversationId)}/read`,
    method: 'put',
  })
}

export function uploadAgencyConversationMedia(conversationId, file) {
  const form = new FormData()
  form.append('file', file)
  return agencyRequest({
    url: `${agencyApiBase}/conversations/${encodeURIComponent(conversationId)}/media`,
    method: 'post',
    data: form,
  })
}

export function downloadAgencyConversationMedia(conversationId, mediaId) {
  return agencyRequest({
    url: `${agencyApiBase}/conversations/${encodeURIComponent(
      conversationId
    )}/media/${encodeURIComponent(mediaId)}`,
    method: 'get',
    responseType: 'blob',
  })
}

export function createAgencyRealtimeTicket() {
  return agencyRequest({
    url: `${agencyApiBase}/realtime-tickets`,
    method: 'post',
  })
}

export function getAgencyInvitations() {
  return agencyRequest({
    url: `${agencyApiBase}/invitations`,
    method: 'get',
  })
}

export function createAgencyInvitation(data) {
  const payload = requirePayloadFields(
    pickDefined(data, [
      'memberUserId',
      'travelId',
      'title',
      'content',
      'workStartDate',
      'workEndDate',
      'expiresAt',
      'clientMessageId',
    ]),
    [
      'memberUserId',
      'travelId',
      'title',
      'content',
      'workStartDate',
      'workEndDate',
      'expiresAt',
      'clientMessageId',
    ]
  )
  return agencyRequest({
    url: `${agencyApiBase}/invitations`,
    method: 'post',
    data: payload,
  })
}

export function cancelAgencyInvitation(invitationId) {
  return agencyRequest({
    url: `${agencyApiBase}/invitations/${encodeURIComponent(invitationId)}/cancel`,
    method: 'put',
  })
}

export function getAgencyJobs(params) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs`,
    method: 'get',
    params: buildTourLeaderJobQuery(params, { includeStatus: true }),
  })
}

export function getAgencyJob(jobId) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}`,
    method: 'get',
  })
}

export function createAgencyJob(data) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs`,
    method: 'post',
    // Tenant and recommendation fields are intentionally stripped. The
    // server derives AgencyCode from the authenticated principal.
    data: toTourLeaderJobPayload(data),
  })
}

export function updateAgencyJob(jobId, data) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}`,
    method: 'put',
    data: toTourLeaderJobPayload(data),
  })
}

export function deleteAgencyJob(jobId) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}`,
    method: 'delete',
  })
}

export function copyAgencyJob(jobId) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}/copy`,
    method: 'post',
  })
}

export function updateAgencyJobStatus(jobId, status) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}/status`,
    method: 'put',
    data: { status: requireJobStatus(status) },
  })
}

export function getAgencyJobApplications(jobId, params) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}/applications`,
    method: 'get',
    params: {
      page: Math.max(1, Number(params && params.page) || 1),
      pageSize: Math.min(
        100,
        Math.max(1, Number(params && (params.pageSize || params.limit)) || 20)
      ),
    },
  })
}

export function updateAgencyJobApplicationStatus(jobId, applicationId, status) {
  return agencyRequest({
    url: `${agencyApiBase}/jobs/${encodeURIComponent(jobId)}/applications/${encodeURIComponent(
      applicationId
    )}/status`,
    method: 'put',
    data: { status: requireApplicationStatus(status) },
  })
}
